import { useApp } from '../../context/AppContext';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Info, PauseCircle } from 'lucide-react';
import { INVESTMENT_TYPES } from '../../utils/taxEngine';

export const ContributionPause = () => {
  const { inputs, updateInput, t, language, investmentType } = useApp();
  if (investmentType === INVESTMENT_TYPES.CREDIT) return null;

  const maxYear = inputs.years || 30;
  const pauseStart = Math.min(inputs.pauseStartYear || 5, maxYear - 1);
  const pauseEnd   = Math.min(inputs.pauseEndYear   || 7, maxYear);

  const handleStartChange = (val) => {
    const s = val[0];
    updateInput('pauseStartYear', s);
    if (s >= (inputs.pauseEndYear || 7)) updateInput('pauseEndYear', Math.min(s + 1, maxYear));
  };

  const handleEndChange = (val) => {
    const e = val[0];
    updateInput('pauseEndYear', e);
    if (e <= (inputs.pauseStartYear || 5)) updateInput('pauseStartYear', Math.max(e - 1, 1));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PauseCircle className="h-4 w-4 text-orange-500" />
          <Label className="text-sm font-semibold cursor-pointer" htmlFor="pause-switch">
            {t('contributionPause')}
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-foreground/50 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-sm">{t('tooltipPause')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Switch
          id="pause-switch"
          checked={inputs.pauseEnabled ?? false}
          onCheckedChange={(c) => updateInput('pauseEnabled', c)}
        />
      </div>

      {inputs.pauseEnabled && (
        <div className="p-5 rounded-2xl bg-orange-100 dark:bg-orange-900/40 border-2 border-orange-200 dark:border-orange-800 space-y-5">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-xs uppercase tracking-[0.12em] text-orange-700 dark:text-orange-300 font-semibold">
                {t('pauseStartYear')}
              </Label>
              <Badge className="bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 font-bold">
                {language === 'ro' ? 'An' : 'Year'} {pauseStart}
              </Badge>
            </div>
            <Slider value={[pauseStart]} onValueChange={handleStartChange}
              min={1} max={Math.max(maxYear - 1, 1)} step={1} className="w-full" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-xs uppercase tracking-[0.12em] text-orange-700 dark:text-orange-300 font-semibold">
                {t('pauseEndYear')}
              </Label>
              <Badge className="bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 font-bold">
                {language === 'ro' ? 'An' : 'Year'} {pauseEnd}
              </Badge>
            </div>
            <Slider value={[pauseEnd]} onValueChange={handleEndChange}
              min={Math.min(2, maxYear)} max={maxYear} step={1} className="w-full" />
          </div>
          <p className="text-xs text-orange-700 dark:text-orange-300">{t('pauseDescription')}</p>
        </div>
      )}
    </div>
  );
};

export default ContributionPause;
